import { DatePicker, TimePicker } from '@mui/lab';
import {
  Card,
  CardActions,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  Typography,
  useEventCallback,
} from '@mui/material';
import { styled } from '@mui/system';
import { FormikProps, useFormik } from 'formik';
import OperatorList from 'front-office/components/shared/operator-list';
import ModalityPaymentList from 'front-office/components/shared/payment/modality-payment-list';
import { DocumentType } from 'front-office/constants/document-type';
import { PAYMENT_TYPE } from 'front-office/pages/released-services/types/cash-check-document-type';
import { DateTime } from 'luxon';
import { memo, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PmiButton } from 'shell/components/shared/button';
import PmiTextField from 'shell/components/shared/text-field';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { __CERTIFICATION_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { ModalityPaymentVM } from 'shell/view-models/modality-payment';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import PaymentList from 'system/components/shared/payment';
import * as yup from 'yup';
import { ObjectShape } from 'yup/lib/object';
import ServiceList from '../list/service-list';

const PmiTitleDivider = styled(Typography)(({ theme }) => ({
  fontSize: '20px',
  color: theme.palette.secondary.main,
}));

export interface CertificateForm {
  acronym?: string;
  year?: string;
  certificationNumber?: string;
  progressive?: string;
  dateForm?: DateTime | null;
  description?: string;
  holderName?: string;
  holderFiscalCode?: string;
  holderPlaceOfBirth?: string;
  holderDateOfBirth?: DateTime;
  applicantName?: string;
  applicantFiscalCode?: string;
  applicantPlaceOfBirth?: string;
  applicantDateOfBirth?: DateTime;
  void?: boolean;
  dateVoid?: DateTime | null;
  timeVoid?: DateTime | null;
  justification?: string;
  voidBy?: string;
  releasedTo?: string;
  birthDate?: DateTime | null;
  releaseMode?: ModalityPaymentVM | null;
  act?: string;
  payment?: string;
  stamp?: string;
  secretary?: string;
  civilStatus?: string;
  fixed?: string;
  refund?: string;
  state?: string;
  numberReceivedPos?: number;
  emitted?: string;
  service?: string;
  date?: DateTime | null;
  time?: DateTime | null;
  reasonVoid?: string;
  certifications?: { key: string; label: string }[];
  numberReceiptPos?: boolean;
  documentCode?: string;
  applicantReleaseReason?: string;
  serviceVoidReason?: string;
  documentType?: string;
  disabledPaymentList?: boolean;
  isApplicantVisible?: boolean;
  isApplicantIsAccountHolder?: boolean;
}

const validationSchema = (isVoid?: boolean) => {
  let schema: ObjectShape = {};
  if (isVoid) {
    schema = {
      justification: yup.string().notRequired().nullable(),
      serviceVoidReason: yup
        .string()
        .nullable()
        .when('justification', {
          is: (justification: string) => justification === 'N',
          then: yup.string().required('Campo obbligatorio'),
          otherwise: yup.string().notRequired(),
        }),
    };
  } else {
    schema = {};
  }

  return yup.object(schema);
};

interface FormCertificateProps {
  onOk: (form?: CertificateForm) => void;
  onGoBack: (form?: CertificateForm) => void;
  title: string;
  children?: ((form?: FormikProps<CertificateForm>) => ReactElement) | never | never[];
  disabled?: boolean;
  isView?: boolean;
  isEdit?: boolean;
  isVoid?: boolean;
  isDelete?: boolean;
  isRestore?: boolean;
  initialValues?: CertificateForm;
}

const FormCertificateComponent = ({
  onOk,
  onGoBack,
  title,
  children,
  disabled = false,
  isEdit,
  isDelete,
  isView,
  isVoid,
  isRestore,
  initialValues,
}: FormCertificateProps): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<boolean>(false);
  const paramsUrl = useParams();

  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const [paymentType, setPaymentType] = useState<string | undefined>();

  useEffect(() => {
    setPaymentType(initialValues?.payment);
  }, []);

  const certificateForm = useFormik<CertificateForm>({
    initialValues: initialValues || {},
    validationSchema: validationSchema(isVoid),
    onSubmit,
  });

  const handleChangeService = useCallback(
    (value) => {
      certificateForm.setFieldValue('service', value);
    },
    [certificateForm.setFieldValue]
  );

  const { userProfile } = useUserProfile();
  const isControlloDiCassa2: boolean = useMemo(() => {
    return userProfile?.datiLogin?.profile?.feature?.controlloDiCassa === '2';
  }, [userProfile?.datiLogin?.profile?.feature?.controlloDiCassa]);

  const handleChangePayment = useEventCallback((value?: string) => {
    if (value === PAYMENT_TYPE.ABSENT) {
      certificateForm.setValues({
        ...certificateForm.values,
        payment: value,
        stamp: '0',
        secretary: '0',
        civilStatus: '0',
        fixed: '0',
        refund: '0',
      });
      return;
    }
    // setPaymentType(value);
    certificateForm.setFieldValue('payment', value);
  });

  const isServiceListAndMotivoRilascioDisabled = useMemo(() => {
    if (isControlloDiCassa2 && isEdit) return false;
    return disabled;
  }, [disabled, isEdit, userProfile?.datiLogin?.profile?.feature?.controlloDiCassa]);

  const isMotivoAnnullamentoDisabled = useMemo(() => {
    if (isVoid) return false;
    return disabled;
  }, [isVoid, disabled]);

  const isPaymentMethodDisabled = useMemo(() => {
    const checkUserProfile = userProfile?.datiLogin?.profile?.feature?.controlloDiCassa;

    const isUserAuthorized =
      checkUserProfile === '1' || checkUserProfile === '0' || checkUserProfile === '2';

    if (isEdit && isUserAuthorized) {
      // if (
      //   (checkUserProfile === '0' || checkUserProfile === '1') &&
      //   initialValues?.payment === PAYMENT_TYPE.POS
      // ) {
      //   return certificateForm.values.numberReceiptPos;
      // }
      return false;
    }
    return disabled;
  }, [
    isEdit,
    disabled,
    initialValues?.payment,
    certificateForm.values.numberReceiptPos,
    userProfile?.datiLogin?.profile?.feature?.controlloDiCassa,
  ]);

  useEffect(() => {
    if (isEdit && isControlloDiCassa2) {
      const paymentValues =
        (initialValues?.payment === PAYMENT_TYPE.CASH ||
          initialValues?.payment === PAYMENT_TYPE.POS) &&
        paymentType === PAYMENT_TYPE.ABSENT
          ? { secretary: '0.00', fixed: '0.00', refund: '0.00' }
          : {
              secretary: initialValues?.secretary,
              fixed: initialValues?.fixed,
              refund: initialValues?.refund,
            };

      certificateForm?.setValues({
        ...(certificateForm?.values || {}),
        secretary: paymentValues?.secretary,
        fixed: paymentValues?.fixed,
        refund: paymentValues?.refund,
      });
    }
  }, [
    paymentType,
    initialValues?.payment,
    isEdit,
    userProfile?.datiLogin?.profile?.feature?.controlloDiCassa,
  ]);

  const disabledDelete: boolean = useMemo(() => {
    return !(isControlloDiCassa2 && certificateForm.values.acronym === 'I');
  }, [isControlloDiCassa2, certificateForm.values.acronym]);

  const deleteData = useHttpClient(
    `${__CERTIFICATION_API_URL__}/certificazione/registri-carte-identita/fisica/${paramsUrl.idSigla}/${paramsUrl.idAnno}/${paramsUrl.progressivo}`,
    {
      options: {
        method: 'DELETE',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const onDelete = useEventCallback(() => {
    (deleteData() as ResponsePromise<void>).then(() => {
      onGoBack(certificateForm.values);
    });
  });

  const isEditable = useMemo(() => {
    return (isControlloDiCassa2 && isEdit) ?? !disabled;
  }, [isControlloDiCassa2, disabled, isEdit]);

  const certificateNumberDynamicLabel = useMemo(() => {
    return certificateForm.values?.documentType === DocumentType.Anpr
      ? 'Numero certificato ANPR'
      : certificateForm.values?.documentType === DocumentType.Ansc
      ? 'Numero certificato ANSC'
      : 'Numero certificato ANPR/ANSC';
  }, [certificateForm.values?.documentType]);

  const isCertificateNumberFieldVisible = useMemo(() => {
    return isView && certificateForm?.values?.certificationNumber;
  }, [isView, certificateForm?.values?.certificationNumber]);

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={certificateForm.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Tipo"
                  label="Tipo"
                  id="type"
                  name="type"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={certificateForm.values.acronym}
                  disabled={disabled}
                  onChange={(event) =>
                    certificateForm.setFieldValue(
                      'acronym',
                      event.target.validity.valid
                        ? event.target.value
                        : certificateForm.values.acronym
                    )
                  }
                  error={certificateForm.touched.acronym && Boolean(certificateForm.errors.acronym)}
                  helperText={certificateForm.errors.acronym}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Anno"
                  label="Anno"
                  id="year"
                  name="year"
                  fullWidth
                  type="number"
                  variant="outlined"
                  disabled={disabled}
                  value={certificateForm.values.year}
                  onChange={(event) =>
                    certificateForm.setFieldValue(
                      'year',
                      event.target.validity.valid
                        ? event.target.value
                        : certificateForm.values.acronym
                    )
                  }
                  error={certificateForm.touched.year && Boolean(certificateForm.errors.year)}
                  helperText={certificateForm.errors.year}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Progressivo"
                  label="Progressivo"
                  id="progressive"
                  name="progressive"
                  fullWidth
                  inputProps={{
                    maxLength: 6,
                    pattern: '[0-9]*',
                  }}
                  type="text"
                  variant="outlined"
                  value={certificateForm.values.progressive}
                  onChange={(event) =>
                    certificateForm.setFieldValue(
                      'progressive',
                      event.target.validity.valid
                        ? event.target.value
                        : certificateForm.values.progressive
                    )
                  }
                  disabled={disabled}
                  error={
                    certificateForm.touched.progressive &&
                    Boolean(certificateForm.errors.progressive)
                  }
                  helperText={certificateForm.errors.progressive}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Emesso il"
                  aria-label="Emesso il"
                  value={certificateForm.values.dateForm || null}
                  onChange={(date) => {
                    certificateForm.setFieldValue('dateForm', date);
                  }}
                  disabled={disabled}
                  renderInput={(params) => (
                    <PmiTextField id="dateForm" {...params} fullWidth helperText="" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  aria-label="Descrizione certificato"
                  label="Descrizione certificato"
                  id="description"
                  name="description"
                  fullWidth
                  type="text"
                  variant="outlined"
                  disabled={disabled}
                  value={certificateForm.values.description || ''}
                  onChange={certificateForm.handleChange}
                  error={
                    certificateForm.touched.description &&
                    Boolean(certificateForm.errors.description)
                  }
                  helperText={certificateForm.errors.description}
                />
              </Grid>
              {isCertificateNumberFieldVisible ? (
                <Grid item xs={12} md={6}>
                  <PmiTextField
                    aria-label={certificateNumberDynamicLabel}
                    label={certificateNumberDynamicLabel}
                    id="certificationNumber"
                    name="certificationNumber"
                    fullWidth
                    type="text"
                    variant="outlined"
                    disabled={disabled}
                    value={certificateForm.values.certificationNumber || ''}
                    onChange={certificateForm.handleChange}
                    error={
                      certificateForm.touched.certificationNumber &&
                      Boolean(certificateForm.errors.certificationNumber)
                    }
                    helperText={certificateForm.errors.certificationNumber}
                  />
                </Grid>
              ) : undefined}
              <Grid item xs={12}>
                <PmiTitleDivider>Intestatario</PmiTitleDivider>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} md={8}>
                <PmiTextField
                  aria-label="bone intestatario"
                  label="Intestatario"
                  id="holderName"
                  name="holderName"
                  fullWidth
                  type="text"
                  variant="outlined"
                  disabled={disabled}
                  value={certificateForm.values.holderName || ''}
                  onChange={certificateForm.handleChange}
                  error={
                    certificateForm.touched.holderName && Boolean(certificateForm.errors.holderName)
                  }
                  helperText={certificateForm.errors.holderName}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  aria-label="Codice fiscale intestatario"
                  label="Codice fiscale"
                  id="holderFiscalCode"
                  name="holderFiscalCode"
                  fullWidth
                  type="text"
                  variant="outlined"
                  disabled={disabled}
                  value={certificateForm.values.holderFiscalCode?.toUpperCase() || ''}
                  onChange={certificateForm.handleChange}
                  error={
                    certificateForm.touched.holderFiscalCode &&
                    Boolean(certificateForm.errors.holderFiscalCode)
                  }
                  helperText={certificateForm.errors.holderFiscalCode}
                />
              </Grid>
              {certificateForm.values?.isApplicantIsAccountHolder ? (
                <>
                  {certificateForm.values?.holderDateOfBirth ? (
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Nato il"
                        aria-label="Data di nascita intestatario"
                        value={certificateForm.values.holderDateOfBirth || null}
                        onChange={(date) => {
                          certificateForm.setFieldValue('holderDateOfBirth', date);
                        }}
                        disabled={disabled}
                        renderInput={(params) => (
                          <PmiTextField
                            id="holderDateOfBirth"
                            {...params}
                            fullWidth
                            helperText=""
                          />
                        )}
                      />
                    </Grid>
                  ) : undefined}
                  {certificateForm.values?.holderPlaceOfBirth ? (
                    <Grid item xs={12} md={8}>
                      <PmiTextField
                        aria-label="luogo di nascita intestatario"
                        label="A"
                        id="holderPlaceOfBirth"
                        name="holderPlaceOfBirth"
                        fullWidth
                        type="text"
                        variant="outlined"
                        disabled={disabled}
                        value={certificateForm.values.holderPlaceOfBirth || ''}
                        onChange={certificateForm.handleChange}
                        error={
                          certificateForm.touched.holderPlaceOfBirth &&
                          Boolean(certificateForm.errors.holderPlaceOfBirth)
                        }
                        helperText={certificateForm.errors.holderPlaceOfBirth}
                      />
                    </Grid>
                  ) : undefined}
                </>
              ) : undefined}
              {certificateForm.values?.isApplicantVisible ? (
                <>
                  <Grid item xs={12}>
                    <PmiTitleDivider>Richiedente</PmiTitleDivider>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <PmiTextField
                      aria-label="Nome richiedente"
                      label="Richiedente"
                      id="applicantName"
                      name="applicantName"
                      fullWidth
                      type="text"
                      variant="outlined"
                      disabled={disabled}
                      value={certificateForm.values.applicantName || ''}
                      onChange={certificateForm.handleChange}
                      error={
                        certificateForm.touched.applicantName &&
                        Boolean(certificateForm.errors.applicantName)
                      }
                      helperText={certificateForm.errors.applicantName}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <PmiTextField
                      aria-label="Codice fiscale richiedente"
                      label="Codice fiscale"
                      id="applicantFiscalCode"
                      name="applicantFiscalCode"
                      fullWidth
                      type="text"
                      variant="outlined"
                      disabled={disabled}
                      value={certificateForm.values.applicantFiscalCode || ''}
                      onChange={certificateForm.handleChange}
                      error={
                        certificateForm.touched.applicantFiscalCode &&
                        Boolean(certificateForm.errors.applicantFiscalCode)
                      }
                      helperText={certificateForm.errors.applicantFiscalCode}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      label="Nato il"
                      aria-label="Data di nascita richiedente"
                      value={certificateForm.values.applicantDateOfBirth || null}
                      onChange={(date) => {
                        certificateForm.setFieldValue('applicantDateOfBirth', date);
                      }}
                      disabled={disabled}
                      renderInput={(params) => (
                        <PmiTextField
                          id="applicantDateOfBirth"
                          {...params}
                          fullWidth
                          helperText=""
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <PmiTextField
                      aria-label="luogo di nascita richiedente"
                      label="A"
                      id="applicantPlaceOfBirth"
                      name="applicantPlaceOfBirth"
                      fullWidth
                      type="text"
                      variant="outlined"
                      disabled={disabled}
                      value={certificateForm.values.applicantPlaceOfBirth || ''}
                      onChange={certificateForm.handleChange}
                      error={
                        certificateForm.touched.applicantPlaceOfBirth &&
                        Boolean(certificateForm.errors.applicantPlaceOfBirth)
                      }
                      helperText={certificateForm.errors.applicantPlaceOfBirth}
                    />
                  </Grid>
                </>
              ) : undefined}
              <Grid item xs={12}>
                <Divider />
              </Grid>
              {children && typeof children === 'function' ? children(certificateForm) : undefined}
              <Grid item xs={12} md={5} lg={3}>
                <ModalityPaymentList
                  aria-label="Modalità rilascio"
                  label="Modalità rilascio"
                  value={certificateForm.values.releaseMode || null}
                  onChange={(el) =>
                    certificateForm.setValues({
                      ...certificateForm.values,
                      releaseMode: el,
                      stamp: el?.item?.importoImpostaBollo || '',
                      secretary: el?.item?.importoDirittiSegreteria || '',
                      civilStatus: el?.item?.importoDirittiStatoCivile || '',
                      fixed: el?.item?.importoDirittiFissi || '',
                      refund: el?.item?.importoSpese || '',
                    })
                  }
                  disabled={!isEditable}
                  error={
                    certificateForm.touched.releaseMode &&
                    Boolean(certificateForm.errors.releaseMode)
                  }
                  helperText={certificateForm.errors.releaseMode}
                />
              </Grid>
              <Grid item xs={12} md={5} lg={3}>
                <PmiTooltipError
                  show={isEdit && certificateForm.values.numberReceiptPos}
                  message={
                    certificateForm.values.numberReceiptPos
                      ? 'non è possibile modificare la modalità di pagamento perchè è stata emessa la ricevuta'
                      : ''
                  }
                >
                  <PaymentList
                    value={certificateForm.values.payment || ''}
                    onChange={handleChangePayment}
                    disabled={
                      isPaymentMethodDisabled || certificateForm.values?.disabledPaymentList
                    }
                    label="Modalità pagamento"
                  />
                </PmiTooltipError>
              </Grid>
              {certificateForm.values.numberReceivedPos &&
              certificateForm.values.numberReceivedPos !== 0 ? (
                <Grid item xs={12} md={5} lg={3}>
                  <PmiTextField
                    aria-label="Numero ricevuta POS"
                    label="Numero ricevuta POS"
                    id="numberReceivedPos"
                    name="numberReceivedPos"
                    fullWidth
                    variant="outlined"
                    value={certificateForm.values.numberReceivedPos || ''}
                    onChange={certificateForm.handleChange}
                    disabled
                    error={
                      certificateForm.touched.numberReceivedPos &&
                      Boolean(certificateForm.errors.numberReceivedPos)
                    }
                    helperText={certificateForm.errors.numberReceivedPos}
                  />
                </Grid>
              ) : undefined}
              <Grid item xs={12} md={5} lg={3}>
                <PmiTextField
                  aria-label="Stato Certificato"
                  label="Stato Certificato"
                  id="state"
                  name="state"
                  fullWidth
                  variant="outlined"
                  value={certificateForm.values.state || ''}
                  onChange={certificateForm.handleChange}
                  disabled={disabled}
                  error={certificateForm.touched.state && Boolean(certificateForm.errors.state)}
                  helperText={certificateForm.errors.state}
                />
              </Grid>
              <Grid item xs={12} md={5} lg={3}>
                <PmiTextField
                  aria-label="Dati"
                  label="Dati"
                  id="act"
                  name="act"
                  fullWidth
                  variant="outlined"
                  value={certificateForm.values.act || ''}
                  onChange={certificateForm.handleChange}
                  disabled={disabled}
                  error={certificateForm.touched.act && Boolean(certificateForm.errors.act)}
                  helperText={certificateForm.errors.act}
                />
              </Grid>
              {certificateForm?.values?.applicantReleaseReason ? (
                <Grid item xs={12}>
                  <PmiTextField
                    aria-label="Motivo"
                    label="Motivo"
                    id="applicantReleaseReason"
                    name="applicantReleaseReason"
                    fullWidth
                    variant="outlined"
                    value={certificateForm.values.applicantReleaseReason || ''}
                    onChange={certificateForm.handleChange}
                    disabled={!isEditable}
                    error={
                      certificateForm.touched.applicantReleaseReason &&
                      Boolean(certificateForm.errors.applicantReleaseReason)
                    }
                    helperText={certificateForm.errors.applicantReleaseReason}
                  />
                </Grid>
              ) : undefined}
              <Grid item xs={12}>
                <PmiTitleDivider>Tipo pagamento</PmiTitleDivider>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} container>
                <Grid item xs={12} md={4}>
                  <PmiTextField
                    aria-label="Imposta di bollo"
                    label="Imposta di bollo"
                    id="stamp"
                    name="stamp"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    }}
                    inputProps={{
                      style: {
                        textAlign: 'right',
                      },
                    }}
                    value={certificateForm.values.stamp || ''}
                    onChange={certificateForm.handleChange}
                    disabled={!isEditable || certificateForm.values.payment === PAYMENT_TYPE.ABSENT}
                    error={certificateForm.touched.stamp && Boolean(certificateForm.errors.stamp)}
                    helperText={certificateForm.errors.stamp}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container>
                <Grid item xs={12} md={4}>
                  <PmiTextField
                    aria-label="Diritti di segreteria"
                    label="Diritti di segreteria"
                    id="secretary"
                    name="secretary"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    }}
                    inputProps={{
                      style: {
                        textAlign: 'right',
                      },
                    }}
                    value={certificateForm.values.secretary || ''}
                    onChange={certificateForm.handleChange}
                    disabled={!isEditable || certificateForm.values.payment === PAYMENT_TYPE.ABSENT}
                    error={
                      certificateForm.touched.secretary && Boolean(certificateForm.errors.secretary)
                    }
                    helperText={certificateForm.errors.secretary}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container>
                <Grid item xs={12} md={4}>
                  <PmiTextField
                    aria-label="Diritti di stato civile"
                    label="Diritti di stato civile"
                    id="civilStatus"
                    name="civilStatus"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    }}
                    inputProps={{
                      style: {
                        textAlign: 'right',
                      },
                    }}
                    value={certificateForm.values.civilStatus || ''}
                    onChange={certificateForm.handleChange}
                    disabled={!isEditable || certificateForm.values.payment === PAYMENT_TYPE.ABSENT}
                    error={
                      certificateForm.touched.civilStatus &&
                      Boolean(certificateForm.errors.civilStatus)
                    }
                    helperText={certificateForm.errors.civilStatus}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container>
                <Grid item xs={12} md={4}>
                  <PmiTextField
                    aria-label="Diritti fissi"
                    label="Diritti fissi"
                    id="fixed"
                    name="fixed"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    }}
                    inputProps={{
                      style: {
                        textAlign: 'right',
                      },
                    }}
                    value={certificateForm.values.fixed || ''}
                    onChange={certificateForm.handleChange}
                    disabled={!isEditable || certificateForm.values.payment === PAYMENT_TYPE.ABSENT}
                    error={certificateForm.touched.fixed && Boolean(certificateForm.errors.fixed)}
                    helperText={certificateForm.errors.fixed}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container>
                <Grid item xs={12} md={4}>
                  <PmiTextField
                    aria-label="Rimborso spese"
                    label="Rimborso spese"
                    id="refund"
                    name="refund"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    }}
                    inputProps={{
                      style: {
                        textAlign: 'right',
                      },
                    }}
                    value={certificateForm.values.refund || ''}
                    onChange={certificateForm.handleChange}
                    disabled={!isEditable || certificateForm.values.payment === PAYMENT_TYPE.ABSENT}
                    error={certificateForm.touched.refund && Boolean(certificateForm.errors.refund)}
                    helperText={certificateForm.errors.refund}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} md={4}>
                <ServiceList
                  label="Servizio emissione"
                  value={certificateForm.values.service || ''}
                  onChange={handleChangeService}
                  disabled={isServiceListAndMotivoRilascioDisabled}
                  error={certificateForm.touched.service && Boolean(certificateForm.errors.service)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <OperatorList
                  aria-label="Operatore emissione"
                  label="Operatore emissione"
                  id="emitted"
                  operatorType="0"
                  value={certificateForm.values.emitted || ''}
                  onChange={(el) => certificateForm.setFieldValue('emitted', el)}
                  disabled={!isEditable}
                  error={certificateForm.touched.emitted && Boolean(certificateForm.errors.emitted)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Emesso il"
                  aria-label="Emesso il"
                  value={certificateForm.values.date || null}
                  disabled={disabled}
                  onChange={(date) => {
                    certificateForm.setFieldValue('date', date);
                  }}
                  renderInput={(params) => (
                    <PmiTextField id="emitted-from" {...params} fullWidth helperText="" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TimePicker
                  label="Ora"
                  value={certificateForm.values.time || null}
                  onChange={(date) => {
                    certificateForm.setFieldValue('time', date);
                  }}
                  disabled={disabled}
                  renderInput={(params) => (
                    <PmiTextField id="time" {...params} fullWidth helperText="" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    aria-label="Giustificativo"
                    id="justification"
                    name="justification"
                    value={
                      certificateForm.values.justification
                        ? String(certificateForm.values.justification)
                        : 'E'
                    }
                    onChange={certificateForm.handleChange}
                  >
                    <FormControlLabel
                      value="E"
                      disabled={isDelete ? false : disabled}
                      control={<Radio />}
                      label="Con giustificativo"
                    />
                    <FormControlLabel
                      value="N"
                      disabled={isDelete ? false : disabled}
                      control={<Radio />}
                      label="Senza giustificativo"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Operatore annullamento"
                  label="Operatore annullamento"
                  id="voidBy"
                  name="voidBy"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={certificateForm.values.voidBy || ''}
                  onChange={certificateForm.handleChange}
                  disabled={disabled}
                  error={certificateForm.touched.voidBy && Boolean(certificateForm.errors.voidBy)}
                  helperText={certificateForm.errors.voidBy}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Annullato il"
                  aria-label="Annullato il"
                  value={certificateForm.values.dateVoid || null}
                  onChange={(date) => {
                    certificateForm.setFieldValue('dateVoid', date);
                  }}
                  disabled={disabled}
                  renderInput={(params) => (
                    <PmiTextField id="dateVoid" {...params} fullWidth helperText="" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TimePicker
                  label="Ora"
                  value={certificateForm.values.timeVoid || null}
                  onChange={(date) => {
                    certificateForm.setFieldValue('timeVoid', date);
                  }}
                  disabled={disabled}
                  renderInput={(params) => (
                    <PmiTextField id="timeVoid" {...params} fullWidth helperText="" />
                  )}
                />
              </Grid>
              {certificateForm?.values?.justification === 'N' ? (
                <Grid item xs={12} md={4}>
                  <PmiTextField
                    aria-label="Motivo annullamento"
                    label="Motivo annullamento"
                    id="serviceVoidReason"
                    name="serviceVoidReason"
                    fullWidth
                    type="text"
                    inputProps={{
                      maxLength: '50',
                    }}
                    variant="outlined"
                    value={certificateForm.values.serviceVoidReason || ''}
                    onChange={certificateForm.handleChange}
                    disabled={isMotivoAnnullamentoDisabled}
                    error={
                      certificateForm.touched.serviceVoidReason &&
                      Boolean(certificateForm.errors.serviceVoidReason)
                    }
                    helperText={certificateForm.errors.serviceVoidReason}
                  />
                </Grid>
              ) : undefined}
              {(certificateForm.values?.certifications || [])?.length > 0 ? (
                <>
                  <Grid item xs={12}>
                    <PmiTitleDivider>Certificato contestuale</PmiTitleDivider>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <List>
                      {(certificateForm.values?.certifications || []).map((certification) => (
                        <ListItem key={certification.key}>
                          <ListItemText primary={certification.label} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </>
              ) : undefined}
            </Grid>
          </div>
        </CardContent>
        <CardActions
          style={{
            padding: '24px 24px 16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <PmiButton
              variant="contained"
              color="primary"
              type="submit"
              disabled={isEdit || isDelete || isRestore || isEditable ? false : disabled}
              style={{ marginRight: '1.5rem' }}
            >
              Conferma
            </PmiButton>
            <PmiButton
              variant="outlined"
              color="cdmBrand"
              onClick={() => {
                onGoBack(certificateForm.values);
              }}
            >
              Indietro
            </PmiButton>
          </div>
          {isDelete ? (
            <PmiButton
              variant="outlined"
              color="cdmBrand"
              onClick={() => {
                setDeleteAlertModal(true);
              }}
              disabled={disabledDelete}
            >
              Cancellazione
            </PmiButton>
          ) : undefined}
        </CardActions>
      </form>
      {deleteAlertModal ? (
        <DeleteAlertModal
          title="Attenzione!"
          message="Si sta effettuando la cancellazione fisica del documento: confermare?"
          open={deleteAlertModal}
          onClose={() => setDeleteAlertModal(false)}
          onYes={onDelete}
          onNo={() => setDeleteAlertModal(false)}
          yesError
        />
      ) : undefined}
    </Card>
  );
};

FormCertificateComponent.displayName = 'Form Certificate component';

const FormCertificate = memo(FormCertificateComponent);

export default FormCertificate as typeof FormCertificateComponent;
